const API_URL = process.env.REACT_APP_API_URL || 'https://api-courses.aviator1903.com/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return await response.json();
  },

  login: async (email, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await response.json();
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  getProfile: async () => {
    const response = await fetch(`${API_URL}/auth/profile`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  },

  verifyEmail: async (token) => {
    const response = await fetch(`${API_URL}/auth/verify-email/${token}`, {
      method: 'GET'
    });
    return await response.json();
  }
};

// Admin API
export const adminAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await response.json();
    if (data.token) {
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
  },

  getDashboardStats: async () => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/dashboard/stats`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  },

  getPendingUsers: async () => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/pending-users`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  },

  sendReminder: async (userId) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/send-reminder/${userId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  },

  getApplications: async () => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/applications`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return await response.json();
  },

  updateApplicationStatus: async (applicationId, status) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/applications/${applicationId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ status })
    });
    return await response.json();
  },

  updateApplicationRemarks: async (applicationId, remarks) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/applications/${applicationId}/remarks`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ remarks })
    });
    return await response.json();
  },

  toggleUniversityForm: async (applicationId, enabled) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/admin/applications/${applicationId}/university-form`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ enabled })
    });
    return await response.json();
  }
};

// Application API
export const applicationAPI = {
  getStatus: async () => {
    const response = await fetch(`${API_URL}/application/status`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  },

  submit: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_URL}/application/submit`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData // FormData object with files
    });
    return await response.json();
  },

  getDetails: async () => {
    const response = await fetch(`${API_URL}/application/details`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  }
};

// Helper to check if user is logged in
export const isAuthenticated = () => {
  return !!getAuthToken();
};

// Helper to check if admin is logged in
export const isAdminAuthenticated = () => {
  return !!localStorage.getItem('adminToken');
};

// Axios-like API instance for easier use
export const api = {
  get: async (url, config = {}) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'GET',
      headers: {
        ...getAuthHeaders(),
        ...config.headers
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return { data: await response.json() };
  },

  post: async (url, data, config = {}) => {
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: isFormData ? {
        ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` }),
        ...config.headers
      } : {
        ...getAuthHeaders(),
        ...config.headers
      },
      body: isFormData ? data : JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return { data: await response.json() };
  },

  put: async (url, data, config = {}) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        ...config.headers
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return { data: await response.json() };
  },

  delete: async (url, config = {}) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        ...config.headers
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return { data: await response.json() };
  }
};

export default api;
